// Article (Knowledge Base) Collection Service
import { createProjectCollectionService, orderBy, where, limit, increment } from '../firebase/firestore';
import type { Article, ArticleStatus } from '@/types';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const articleService = createProjectCollectionService<Article>('articles');

export const articlesService = {
  ...articleService,

  // Get published articles (for public portal)
  getPublished: async (orgId: string, projectId: string) => {
    return articleService.getByProject(orgId, projectId, [
      where('status', '==', 'published'),
      orderBy('updatedAt', 'desc'),
    ]);
  },

  // Get articles by status (for admin)
  getByStatus: async (orgId: string, projectId: string, status: ArticleStatus) => {
    return articleService.getByProject(orgId, projectId, [
      where('status', '==', status),
      orderBy('updatedAt', 'desc'),
    ]);
  },

  // Get articles by category
  getByCategory: async (orgId: string, projectId: string, category: string) => {
    return articleService.getByProject(orgId, projectId, [
      where('status', '==', 'published'),
      where('category', '==', category),
      orderBy('updatedAt', 'desc'),
    ]);
  },

  // Search articles by title (basic - for advanced search use Algolia/Typesense)
  searchByTitle: async (orgId: string, projectId: string, searchTerm: string) => {
    // Firestore doesn't support full-text search natively
    // This is a workaround using array-contains with keywords
    // For production, integrate Algolia or Typesense
    const articles = await articleService.getByProject(orgId, projectId, [
      where('status', '==', 'published'),
    ]);

    const term = searchTerm.toLowerCase();
    return articles.filter(
      (article) =>
        article.title.toLowerCase().includes(term) ||
        article.content.toLowerCase().includes(term) ||
        article.tags?.some((tag) => tag.toLowerCase().includes(term))
    );
  },

  // Get popular articles
  getPopular: async (orgId: string, projectId: string, count = 5) => {
    return articleService.getByProject(orgId, projectId, [
      where('status', '==', 'published'),
      orderBy('viewCount', 'desc'),
      limit(count),
    ]);
  },

  // Increment view count
  incrementViews: async (articleId: string) => {
    const docRef = doc(db, 'articles', articleId);
    await updateDoc(docRef, {
      viewCount: increment(1),
    });
  },

  // Mark as helpful
  markHelpful: async (articleId: string, helpful: boolean) => {
    const docRef = doc(db, 'articles', articleId);
    await updateDoc(docRef, {
      helpfulCount: increment(helpful ? 1 : 0),
      notHelpfulCount: increment(helpful ? 0 : 1),
    });
  },

  // Publish article
  publish: async (articleId: string) => {
    return articleService.update(articleId, { status: 'published' });
  },

  // Unpublish article (set to draft)
  unpublish: async (articleId: string) => {
    return articleService.update(articleId, { status: 'draft' });
  },

  // Archive article
  archive: async (articleId: string) => {
    return articleService.update(articleId, { status: 'archived' });
  },

  // Get all categories for a project
  getCategories: async (orgId: string, projectId: string): Promise<string[]> => {
    const articles = await articleService.getByProject(orgId, projectId, [
      where('status', '==', 'published'),
    ]);
    const categories = new Set(articles.map((a) => a.category).filter(Boolean));
    return Array.from(categories);
  },
};
